/**
 * Reading Summary Panel
 * 
 * Displays the generated reading session summary in a beautiful,
 * age-appropriate floating panel with achievement badges, highlights,
 * tips, and download options.
 */

import React, { useEffect, useState } from 'react';
import { summaryService, ReaderSummary } from '../services/summaryService';

interface ReadingSummaryPanelProps {
  storySessionId: string;
  userAge: number;
  isOpen: boolean;
  onClose: () => void;
}

const BADGE_STYLES = {
  excellent: {
    emoji: '🏆',
    text: 'Excellent Reader!',
    gradient: 'from-yellow-400 to-orange-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-300',
    textColor: 'text-yellow-800'
  },
  great: {
    emoji: '⭐',
    text: 'Great Job!',
    gradient: 'from-blue-400 to-purple-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-800'
  },
  good: {
    emoji: '👍',
    text: 'Good Work!',
    gradient: 'from-green-400 to-teal-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
    textColor: 'text-green-800'
  },
  keep_trying: {
    emoji: '💪',
    text: 'Keep Practicing!',
    gradient: 'from-pink-400 to-red-500',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-300',
    textColor: 'text-pink-800'
  }
};

export default function ReadingSummaryPanel({
  storySessionId,
  userAge,
  isOpen,
  onClose
}: ReadingSummaryPanelProps) {
  const [summary, setSummary] = useState<ReaderSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await summaryService.getSummary(storySessionId);
        setSummary(response.reader_summary);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching summary:', err);
        setError(err.message || 'Failed to load summary');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [isOpen, storySessionId]);

  const handleDownload = async (format: 'txt' | 'pdf') => {
    try {
      setDownloading(true);
      await summaryService.downloadSummary(storySessionId, format, 'reader');
    } catch (err: any) {
      console.error('Error downloading summary:', err);
      alert(`Failed to download summary: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  const badgeStyle = summary 
    ? BADGE_STYLES[summary.achievement_badge] || BADGE_STYLES.good
    : BADGE_STYLES.good;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${badgeStyle.gradient} p-6 text-white relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all z-10"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {loading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold">Loading Your Summary...</h2>
            </div>
          ) : error ? (
            <div className="text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold">Oops!</h2>
              <p className="mt-2">{error}</p>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-6xl mb-4 animate-bounce">{badgeStyle.emoji}</div>
              <h2 className="text-3xl font-bold mb-2">{badgeStyle.text}</h2>
              <p className="text-lg opacity-90">Your Reading Session Summary</p>
            </div>
          )}
        </div>

        {/* Content */}
        {!loading && !error && summary && (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Overall Performance */}
            <div className={`${badgeStyle.bgColor} ${badgeStyle.borderColor} border-2 rounded-lg p-4`}>
              <h3 className={`font-bold text-lg ${badgeStyle.textColor} mb-2 flex items-center gap-2`}>
                <span>🎯</span>
                <span>Your Performance</span>
              </h3>
              <p className={`${badgeStyle.textColor} text-base leading-relaxed`}>
                {summary.overall_performance}
              </p>
            </div>

            {/* Reading Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                <div className="text-3xl mb-2">📖</div>
                <div className="text-2xl font-bold text-blue-900">{summary.words_read}</div>
                <div className="text-sm text-blue-700">Words Read</div>
              </div>
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 text-center">
                <div className="text-3xl mb-2">⏱️</div>
                <div className="text-lg font-bold text-purple-900">{summary.reading_duration}</div>
                <div className="text-sm text-purple-700">Reading Time</div>
              </div>
            </div>

            {/* Quiz Score */}
            {summary.quiz_score && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <h3 className="font-bold text-lg text-green-800 mb-2 flex items-center gap-2">
                  <span>📝</span>
                  <span>Quiz Results</span>
                </h3>
                <p className="text-green-700 text-base">
                  {typeof summary.quiz_score === 'string' 
                    ? summary.quiz_score 
                    : summary.quiz_score.score_text || `${summary.quiz_score.correct_answers} out of ${summary.quiz_score.total_questions} correct!`}
                </p>
              </div>
            )}

            {/* Roleplay Participation */}
            {summary.roleplay_participation && (
              <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
                <h3 className="font-bold text-lg text-indigo-800 mb-2 flex items-center gap-2">
                  <span>🎭</span>
                  <span>Roleplay</span>
                </h3>
                <p className="text-indigo-700 text-base">
                  {typeof summary.roleplay_participation === 'string'
                    ? summary.roleplay_participation
                    : summary.roleplay_participation.participation_text || 'Great roleplay participation!'}
                </p>
              </div>
            )}

            {/* Pronunciation Highlights */}
            {summary.pronunciation_highlights && summary.pronunciation_highlights.length > 0 && (
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                <h3 className="font-bold text-lg text-yellow-800 mb-3 flex items-center gap-2">
                  <span>🎯</span>
                  <span>Pronunciation Highlights</span>
                </h3>
                <ul className="space-y-2">
                  {summary.pronunciation_highlights.map((highlight, index) => {
                    const text = typeof highlight === 'string' 
                      ? highlight 
                      : `${highlight.word}: ${highlight.tip || highlight.status}`;
                    return (
                      <li key={index} className="flex items-start gap-2 text-yellow-800">
                        <span className="text-yellow-600 mt-1">•</span>
                        <span className="text-base">{text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Improvement Tips */}
            {summary.improvement_tips && summary.improvement_tips.length > 0 && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                <h3 className="font-bold text-lg text-orange-800 mb-3 flex items-center gap-2">
                  <span>💡</span>
                  <span>Tips for Next Time</span>
                </h3>
                <ul className="space-y-2">
                  {summary.improvement_tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-2 text-orange-800">
                      <span className="text-orange-600 mt-1">•</span>
                      <span className="text-base">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Encouragement Message */}
            <div className={`bg-gradient-to-r ${badgeStyle.gradient} rounded-lg p-6 text-white text-center`}>
              <div className="text-4xl mb-3">🌟</div>
              <p className="text-lg font-medium leading-relaxed">
                {summary.encouragement_message}
              </p>
            </div>
          </div>
        )}

        {/* Footer with Download Buttons */}
        {!loading && !error && summary && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => handleDownload('txt')}
                disabled={downloading}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-all shadow-md font-medium"
              >
                {downloading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span>Download Text</span>
                  </>
                )}
              </button>

              <button
                onClick={() => handleDownload('pdf')}
                disabled={downloading}
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-all shadow-md font-medium"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
                <span>Download PDF</span>
              </button>

              <button
                onClick={onClose}
                className="flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-all shadow-md font-medium"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Close</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
